import { useQuery, useQueryClient } from '@tanstack/react-query';
import { components, paths } from 'src/generated/v1';
import { $api, $apiFetchClient } from './api';

export type CountryExt = components['schemas']['CountryExt'];
// export type Country = components['schemas']['Country'];

export const getGroupCountries = async (countryGroupId: number) => {
  const resp = await $apiFetchClient.GET('/country/groups/{country_group_id}/countries', {
    params: {
      path: {
        country_group_id: countryGroupId,
      },
    },
  });

  if (!resp.data) {
    throw new Error('error fetching group countries');
  }

  return resp.data;
};

export const useOneCountryGroupQuery = (countryGroupId: number, options?: { enabled?: boolean }) => {
  return $api.useQuery(
    'get',
    '/country/groups/{country_group_id}',
    {
      params: {
        path: {
          country_group_id: countryGroupId,
        },
      },
    },
    options,
  );
};

export const useGroupCountriesQuery = (countryGroupId: number, options?: { enabled?: boolean }) => {
  return $api.useQuery(
    'get',
    '/country/groups/{country_group_id}/countries',
    {
      params: {
        path: {
          country_group_id: countryGroupId,
        },
      },
    },
    options,
  );
};

export const useAllCountriesQuery = (params: paths['/country/country']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/country/country', {
    params: {
      query: params,
    },
  });
};

export const useAllCountriesForSelectionQuery = () => {
  return $api.useQuery('get', '/country');
};

/**
 * Full, unfiltered country list (every country, paged through /country/country).
 * Unlike `useAllCountriesForSelectionQuery` (GET /country), this does NOT exclude
 * countries already assigned to a country group, so e.g. Austria/Germany are
 * included. `size` is capped at 100, so we page until all are collected.
 */
export const useAllCountriesListQuery = () => {
  return useQuery({
    queryKey: ['all-countries-list'],
    queryFn: async () => {
      const all: CountryExt[] = [];
      let page = 1;
      let pages = 1;
      do {
        // eslint-disable-next-line no-await-in-loop
        const resp = await $apiFetchClient.GET('/country/country', {
          params: { query: { page, size: 100 } },
        });
        if (!resp.data) break;
        all.push(...resp.data.items);
        pages = resp.data.pages;
        page += 1;
      } while (page <= pages);
      return all;
    },
  });
};

export const useAllCountryGroupsQuery = (params: paths['/country/groups']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/country/groups', {
    params: {
      query: params,
    },
  });
};

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  const keys = [
    ['get', '/country/groups/{country_group_id}'],
    ['get', '/country/groups/{country_group_id}/countries'],
    ['get', '/country/country'],
    ['get', '/country'],
    ['get', '/country/groups'],
    ['countries'], // custom key used elsewhere
  ];
  keys.forEach(key => {
    queryClient.invalidateQueries({ queryKey: key });
  });
}

export const useCountryGroupUpdate = () => {
  const queryClient = useQueryClient();

  const updateMutation = $api.useMutation('put', '/country/country/{country_id}/group/{country_group_id}', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return updateMutation;
};

export const useCountryGroupRemove = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/utility/bulk_action/{model}/remove_group', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const useCountryRemove = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/utility/bulk_action/{model}/cancel', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const useCountryActivate = () => {
  const queryClient = useQueryClient();

  const activateMutation = $api.useMutation('put', '/country/country/{country_id}/activate/{set_active}', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return activateMutation;
};

export const useCountryGroupArchive = () => {
  const queryClient = useQueryClient();
  const removeMutation = $api.useMutation('delete', '/country/groups/{country_group_id}', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return removeMutation;
};

export const useCountryGroupCreate = () => {
  const queryClient = useQueryClient();

  const createMutation = $api.useMutation('post', '/country/groups', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return createMutation;
};

export const useCountryGroupUpdateData = () => {
  const queryClient = useQueryClient();

  const updateMutation = $api.useMutation('put', '/country/groups/{country_group_id}', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return updateMutation;
};
